/**
 * Minimal shape of a material group needed for the material-group dialog filter. Using a structural type
 * (rather than importing the full MaterialGroup class) keeps this module — and its spec —
 * independent of the @lib/material-group barrel, which transitively pulls heavy modules that
 * aren't loadable in Jest's sandbox.
 */
export interface IFilterableMaterialGroup {
    readonly id: string;
    readonly code: string;
    readonly primaryName: string;
    readonly status: string;
}

export type MaterialGroupItemStatus = 'free' | 'own' | 'blocked';

export interface IOccupationOwner {
    readonly code: string;
    readonly name: string;
}

export interface IMaterialGroupItem<T extends IFilterableMaterialGroup> {
    readonly materialGroup: T;
    readonly status: MaterialGroupItemStatus;
    readonly owner: IOccupationOwner | null;
}

const ACTIVE_STATUS = 'active';

/**
 * Builds the unified list rendered in the material-group dialog. Each material group is annotated with one of:
 *  - `own`     — already assigned to the current purchasing group (pre-checked, removable)
 *  - `blocked` — already assigned to another purchasing group (disabled, owner shown)
 *  - `free`    — not yet assigned anywhere (checkable, will be added on save)
 *
 * Deactivated material groups that are not currently assigned to the current purchasing group are
 * dropped (you can't add inactive groups; if you already own one, it stays visible so you can still
 * remove it). The filter text matches case-insensitively on code or primary name. Results are
 * sorted by code in natural numeric order.
 */
export function buildMaterialGroupItems<T extends IFilterableMaterialGroup>(
    allMaterialGroups: ReadonlyArray<T>,
    ownMaterialGroupIds: ReadonlySet<string>,
    occupationsByMaterialGroupId: ReadonlyMap<string, IOccupationOwner>,
    filterText: string,
): ReadonlyArray<IMaterialGroupItem<T>> {
    const needle = filterText.trim().toLowerCase();
    return allMaterialGroups
        .filter((mg) => {
            if (mg.status !== ACTIVE_STATUS && !ownMaterialGroupIds.has(mg.id)) return false;
            if (!needle) return true;
            const name = (mg.primaryName || '').toLowerCase();
            return mg.code.toLowerCase().includes(needle) || name.includes(needle);
        })
        .map((mg) => {
            if (ownMaterialGroupIds.has(mg.id)) {
                return { materialGroup: mg, status: 'own' as const, owner: null };
            }
            const owner = occupationsByMaterialGroupId.get(mg.id);
            if (owner) {
                return { materialGroup: mg, status: 'blocked' as const, owner };
            }
            return { materialGroup: mg, status: 'free' as const, owner: null };
        })
        .sort((a, b) =>
            a.materialGroup.code.localeCompare(b.materialGroup.code, undefined, {
                numeric: true,
                sensitivity: 'base',
            }),
        );
}
